import React, { useState, useEffect } from 'react';
import { Eye, CheckCircle, XCircle, Clock, Download, X, ThumbsUp, ThumbsDown } from 'lucide-react';
import { admissionsService } from '../../../firebase/admissions';
import { useRealtime } from '../../../contexts/RealtimeContext';
import { generateApplicationReceipt, generateApplicationReceiptBlob } from '../../../utils/receiptGenerator';
import InbuiltPDFViewer from '../../../components/InbuiltPDFViewer';
import toast from 'react-hot-toast';

const ViewApplications = () => {
  const { userProfile } = useRealtime();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewReceiptState, setViewReceiptState] = useState({ open: false, url: null, title: null });

  useEffect(() => {
    loadApplications();
  }, []);

  const loadApplications = async () => {
    try {
      setLoading(true);
      const institutionId = userProfile?.coachingId || userProfile?.institutionId;
      if (!institutionId) {
        setLoading(false);
        return;
      }

      const result = await admissionsService.getInstitutionApplications(institutionId);
      if (result.success) {
        setApplications(result.applications || []);
      } else {
        toast.error('Failed to load applications: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error loading applications: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { icon: Clock, color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200', label: 'Pending' },
      approved: { icon: CheckCircle, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Admitted' },
      admitted: { icon: CheckCircle, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Admitted' },
      rejected: { icon: XCircle, color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200', label: 'Rejected' },
      refund_pending: { icon: Clock, color: 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200', label: 'Refund Pending' },
      refunded: { icon: CheckCircle, color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200', label: 'Refunded' },
      submitted: { icon: Clock, color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200', label: 'Submitted' }
    };
    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;
    return (
      <span className={`${badge.color} px-3 py-1 rounded-full text-sm flex items-center gap-1`}>
        <Icon className="w-4 h-4" />
        {badge.label}
      </span>
    );
  };

  const handleAcceptApplication = async (application) => {
    if (!window.confirm(`Are you sure you want to accept ${application.firstName} ${application.lastName}'s application?`)) {
      return;
    }

    try {
      const result = await admissionsService.updateApplicationStatus(application.id, 'admitted', 'Application accepted by institution');
      if (result.success) {
        toast.success('Application accepted successfully');
        loadApplications();
      } else {
        toast.error('Failed to accept application: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error accepting application: ' + error.message);
    }
  };

  const handleRejectApplication = async (application) => {
    if (!window.confirm(`Are you sure you want to reject ${application.firstName} ${application.lastName}'s application? A refund will be processed.`)) {
      return;
    }

    try {
      const result = await admissionsService.updateApplicationStatus(application.id, 'rejected', 'Application rejected by institution');
      if (result.success) {
        toast.success('Application rejected. Student will be notified about the refund process.');
        loadApplications();
      } else {
        toast.error('Failed to reject application: ' + (result.error || 'Unknown error'));
      }
    } catch (error) {
      toast.error('Error rejecting application: ' + error.message);
    }
  };

  const handleDownloadReceipt = async (application) => {
    try {
      // Load institution data
      const instResult = await admissionsService.getInstitutionById(application.institutionId);
      if (!instResult.success) {
        toast.error('Failed to load institution data');
        return;
      }

      const institution = instResult.institution;
      
      // Generate receipt
      const { generateApplicationReceipt } = await import('../../../utils/receiptGenerator');
      await generateApplicationReceipt(
        application,
        institution,
        application.paymentTransactionId || 'N/A',
        {
          amount: application.paymentAmount || institution.applicationFee || 0,
          status: application.paymentStatus || 'completed'
        }
      );
      
      toast.success('Receipt downloaded successfully');
    } catch (error) {
      toast.error('Failed to generate receipt: ' + error.message);
    }
  };

  const handleViewReceipt = async (application) => {
    try {
      // Load institution data
      const instResult = await admissionsService.getInstitutionById(application.institutionId);
      if (!instResult.success) {
        toast.error('Failed to load institution data');
        return;
      }

      const institution = instResult.institution;
      
      // Generate receipt as blob URL for viewing
      const pdfUrl = await generateApplicationReceiptBlob(
        application,
        institution,
        application.paymentTransactionId || 'N/A',
        {
          amount: application.paymentAmount || institution.applicationFee || 0,
          status: application.paymentStatus || 'completed'
        }
      );
      
      setViewReceiptState({
        open: true,
        url: pdfUrl,
        title: `Receipt - ${application.firstName} ${application.lastName}`
      });
    } catch (error) {
      toast.error('Failed to generate receipt: ' + error.message);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Applications</h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          View and manage student applications
        </p>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden">
        {applications.length === 0 ? (
          <div className="p-12 text-center">
            <p className="text-gray-600 dark:text-gray-400">No applications yet</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Phone</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Status</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Actions</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase">Receipt</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {applications.map((app) => (
                  <tr key={app.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {app.firstName} {app.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.phone}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getStatusBadge(app.status)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600 dark:text-gray-400">
                      {app.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      <div className="flex items-center gap-3">
                        {(app.status === 'pending' || app.status === 'submitted') && (
                          <>
                            <button
                              onClick={() => handleAcceptApplication(app)}
                              className="text-green-600 hover:text-green-800 dark:text-green-400 dark:hover:text-green-300"
                              title="Accept Application"
                            >
                              <ThumbsUp className="w-5 h-5" />
                            </button>
                            <button
                              onClick={() => handleRejectApplication(app)}
                              className="text-red-600 hover:text-red-800 dark:text-red-400 dark:hover:text-red-300"
                              title="Reject Application"
                            >
                              <ThumbsDown className="w-5 h-5" />
                            </button>
                          </>
                        )}
                        {app.paymentTransactionId && (
                          <button
                            onClick={() => handleViewReceipt(app)}
                            className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                            title="View Receipt"
                          >
                            <Eye className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm">
                      {app.paymentTransactionId && (
                        <button
                          onClick={() => handleDownloadReceipt(app)}
                          className="text-green-600 hover:text-green-800"
                          title="Download Receipt"
                        >
                          <Download className="w-5 h-5" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* PDF Viewer Modal for Receipt */}
      {viewReceiptState.open && viewReceiptState.url && (
        <InbuiltPDFViewer
          url={viewReceiptState.url}
          title={viewReceiptState.title}
          onClose={() => {
            // Clean up the blob URL
            if (viewReceiptState.url && viewReceiptState.url.startsWith('blob:')) {
              URL.revokeObjectURL(viewReceiptState.url);
            }
            setViewReceiptState({ open: false, url: null, title: null });
          }}
        />
      )}
    </div>
  );
};

export default ViewApplications;

